import React, { Fragment, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    TextField,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../context/AppContext";
import { registerUser } from "../api/users";
import LanguageToggle from "../components/LanguageToggle";

const Register = () => {
    const theme = useTheme();
    const [form, setForm] = useState({
        name: "",
        email: "",
        password: "",
        retypePassword: "",
    });
    const [errors, setErrors] = useState({});
    const { setIsLoading } = useAppContext();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [submitted, setSubmitted] = useState(false);

    const handleChange = (e) => {
        setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
        setErrors((prev) => ({ ...prev, [e.target.name]: null }));
    };

    const isValidEmail = (email) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

    const handleRegister = async () => {
        setIsLoading(true);
        let newErrors = {};

        if (!form.name.trim()) newErrors.name = t("errors.required_name");
        if (!form.email.trim()) newErrors.email = t("errors.required_email");
        else if (!isValidEmail(form.email)) newErrors.email = t("errors.invalid_email");

        if (!form.password.trim()) {
            newErrors.password = t("errors.required_password");
        } else if (!/^(?=.*[a-z])(?=.*[A-Z]).{8,}$/.test(form.password)) {
            newErrors.password = t("errors.weak_password");
        }
        if (!form.retypePassword.trim()) newErrors.retypePassword = t("errors.required_retype_password");
        else if (form.password !== form.retypePassword)
            newErrors.retypePassword = t("errors.passwords_do_not_match");

        setErrors(newErrors);
        const hasErrors = Object.keys(newErrors).length > 0;

        if (hasErrors) {
            setIsLoading(false);
            return;
        }

        try {
            await registerUser(form.name, form.email, form.password);
            // navigate("/login");
            setSubmitted(true);
        } catch (error) {
            console.error("Registration failed:", error);
            setErrors({ email: t(`errors.${error.message}`) });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                bgcolor: theme.palette.background.default,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 2,
            }}
        >
            <Card
                sx={{
                    maxWidth: 400,
                    width: "100%",
                    bgcolor: theme.palette.sidebar?.background || theme.palette.background.paper,
                    boxShadow: 6,
                    borderRadius: 2,
                }}
            >
                <CardContent>
                    {submitted ? (
                        <>
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                textAlign="center"
                                color={theme.palette.primary.main}
                                mb={2}
                            >
                                {t("register.successTitle")}
                            </Typography>
                            <Typography
                                variant="body1"
                                textAlign="center"
                                mb={3}
                                color={theme.palette.text.primary}
                            >
                                {t("register.successMessage")}
                            </Typography>

                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => navigate("/login")}
                                sx={{
                                    fontWeight: 500,
                                    borderRadius: 2,
                                    textTransform: "none",
                                }}
                            >
                                {t("register.backToLogin")}
                            </Button>
                        </>
                    ) : (
                        <Fragment>
                            <Typography variant="h5" fontWeight="bold" mb={3} textAlign="center">
                                {t("register.title")}
                            </Typography>
                            <LanguageToggle />
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleRegister();
                                }}
                            >
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    label={t("register.name")}
                                    name="name"
                                    value={form.name}
                                    onChange={handleChange}
                                    error={!!errors.name}
                                    helperText={errors.name}
                                    sx={{ mb: 2 }}
                                    InputLabelProps={{ style: { color: theme.palette.text.primary } }}
                                    InputProps={{ style: { color: theme.palette.text.primary } }}
                                />

                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    label={t("register.email")}
                                    name="email"
                                    value={form.email}
                                    onChange={handleChange}
                                    error={!!errors.email}
                                    helperText={errors.email}
                                    sx={{ mb: 2 }}
                                    InputLabelProps={{ style: { color: theme.palette.text.primary } }}
                                    InputProps={{ style: { color: theme.palette.text.primary } }}
                                />

                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    label={t("register.password")}
                                    name="password"
                                    type="password"
                                    value={form.password}
                                    onChange={handleChange}
                                    error={!!errors.password}
                                    helperText={errors.password}
                                    sx={{ mb: 2 }}
                                    InputLabelProps={{ style: { color: theme.palette.text.primary } }}
                                    InputProps={{ style: { color: theme.palette.text.primary } }}
                                />

                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    label={t("register.retypePassword")}
                                    name="retypePassword"
                                    type="password"
                                    value={form.retypePassword}
                                    onChange={handleChange}
                                    error={!!errors.retypePassword}
                                    helperText={errors.retypePassword}
                                    sx={{ mb: 3 }}
                                    InputLabelProps={{ style: { color: theme.palette.text.primary } }}
                                    InputProps={{ style: { color: theme.palette.text.primary } }}
                                />

                                <Button
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    type="submit"
                                    sx={{
                                        fontWeight: 600,
                                        textTransform: "none",
                                        borderRadius: 2,
                                    }}
                                >
                                    {t("register.submit")}
                                </Button>
                            </form>
                        </Fragment>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default Register;
